// ============================================================
// TMR Portal Admin — composing role grants (06 §5.1), shared by every screen that hands out access
// ============================================================
import { useRef, useState } from 'react';
import { Btn, Segmented } from '../../components/primitives';
import { SelectField, TextField } from '../../components/fields';
import { RecordPicker } from '../../components/RecordPicker';
import { Icons, StrokeIcon } from '../../lib/icons';
import { PERMISSIONS, useOperatorScope, usePermission } from '../../data/PermissionsProvider';
import { useCompanySearch } from '../../data/companies/queries';
import { useHoldingSearch } from '../../data/authorization/queries';
import type { AccessRoleGrant, AuthorizationRole, HoldingOption } from '../../data/authorization/contract';
import type { CompanyListItem } from '../../data/companies/contract';

/**
 * How wide a grant reaches. Company and Holding are mutually exclusive on the wire; `''` is a scope the
 * operator has not chosen yet, which is a state the form has to be able to hold — see {@link newGrantDraft}.
 */
export type GrantScope = '' | 'global' | 'company' | 'holding';

/** A grant being assembled — it carries the picked record's name so the operator sees what they chose. */
export interface GrantDraft {
  /** Stable key for the row, since two drafts may share a role while being edited. */
  key: string;
  roleId: string;
  scope: GrantScope;
  companyId: string | null;
  companyName: string | null;
  holdingId: string | null;
  holdingName: string | null;
  /** `YYYY-MM-DD` from the date input, or empty for an indefinite grant. */
  expiresOn: string;
}

/**
 * An empty draft. Scope starts **unchosen**, never `global`: the widest reach has to be something the
 * operator asked for, or picking a role would be two clicks from portal-wide access with nothing said.
 */
export function newGrantDraft(key: string): GrantDraft {
  return {
    key,
    roleId: '',
    scope: '',
    companyId: null,
    companyName: null,
    holdingId: null,
    holdingName: null,
    expiresOn: '',
  };
}

/** What is wrong with a draft, or null when it is ready to send. */
export function grantDraftError(grant: GrantDraft): string | null {
  if (!grant.roleId) return 'Escolha o papel a conceder.';
  if (!grant.scope) return 'Escolha o alcance da concessão.';
  if (grant.scope === 'company' && !grant.companyId) return 'Escolha a Empresa do escopo.';
  if (grant.scope === 'holding' && !grant.holdingId) return 'Escolha a Holding do escopo.';
  if (grant.expiresOn && !isFuture(grant.expiresOn)) return 'A validade precisa ser uma data futura.';
  return null;
}

/** Turns a draft into the wire shape: one scope at most, and an instant the backend can compare. */
export function toGrant(grant: GrantDraft): AccessRoleGrant {
  return {
    roleId: grant.roleId,
    companyId: grant.scope === 'company' ? grant.companyId : null,
    holdingId: grant.scope === 'holding' ? grant.holdingId : null,
    // The date input yields a day; the grant should last through it, so it expires at its end.
    expiresAt: grant.expiresOn ? new Date(`${grant.expiresOn}T23:59:59`).toISOString() : null,
  };
}

/** Whether a `YYYY-MM-DD` day still lies ahead — the backend rejects an expiry that is already past. */
function isFuture(day: string): boolean {
  const end = new Date(`${day}T23:59:59`);
  return !Number.isNaN(end.getTime()) && end.getTime() > Date.now();
}

/** Props for {@link GrantsEditor}. */
interface GrantsEditorProps {
  /** The drafts being edited; controlled, so the parent owns them and can submit. */
  grants: GrantDraft[];
  onChange: (grants: GrantDraft[]) => void;
  /** Roles available to grant — the same catalogue the Papéis screen reads. */
  roles: AuthorizationRole[];
  /** Whether to show per-row validation. Parents flip this on the first rejected submit. */
  showErrors?: boolean;
  /** What to say when no grant has been added yet; the consequence differs per screen. */
  emptyHint: string;
  /** Label for the add button. */
  addLabel?: string;
}

/**
 * Composes a list of role grants, with the scope and expiry each one carries (06 §5).
 *
 * Which scopes are on offer follows two different limits. A picker only appears when the operator can read
 * that directory — one that 403s on every keystroke is worse than none (skill `frontend-architecture`,
 * Regra 5). And **the broad scope is only offered to an operator who is themselves unscoped**:
 * `RoleGrantValidator` refuses a null `companyId` from anyone else (06 §5.1), so showing it to a scoped
 * operator would offer them the one option every submission is going to refuse.
 *
 * Anti-escalation on the role itself stays the server's: it compares each role against the operator's fresh
 * permissions, and the 403 names the role that went too far. The client never tries to predict it.
 */
export function GrantsEditor({ grants, onChange, roles, showErrors = false, emptyHint, addLabel = 'Adicionar papel' }: GrantsEditorProps) {
  const canReadCompanies = usePermission(PERMISSIONS.companyRead);
  const canManageUsers = usePermission(PERMISSIONS.usersManage);
  const canGrantGlobally = useOperatorScope()?.allEmpresas ?? false;

  const [picking, setPicking] = useState<{ key: string; scope: 'company' | 'holding' } | null>(null);
  // A counter, not the row index: removing the first row would otherwise shift every key below it and
  // React would reuse the wrong inputs.
  const nextKey = useRef(0);

  const patch = (key: string, changes: Partial<GrantDraft>) =>
    onChange(grants.map((grant) => (grant.key === key ? { ...grant, ...changes } : grant)));

  const add = () => {
    nextKey.current += 1;
    onChange([...grants, newGrantDraft(`grant-${nextKey.current}`)]);
  };

  return (
    <>
      {grants.length === 0 ? (
        <p style={{ margin: 0, fontSize: 13.5, color: 'var(--fg-muted)', lineHeight: 1.5 }}>{emptyHint}</p>
      ) : (
        grants.map((grant) => (
          <GrantRow
            key={grant.key}
            grant={grant}
            roles={roles}
            error={showErrors ? grantDraftError(grant) : null}
            canReadCompanies={canReadCompanies}
            canManageUsers={canManageUsers}
            canGrantGlobally={canGrantGlobally}
            onChange={(changes) => patch(grant.key, changes)}
            onPick={(scope) => setPicking({ key: grant.key, scope })}
            onRemove={() => onChange(grants.filter((other) => other.key !== grant.key))}
          />
        ))
      )}

      <div>
        <Btn variant="secondary" icon={Icons.plus} onClick={add}>{addLabel}</Btn>
      </div>

      {picking?.scope === 'company' && (
        <RecordPicker<CompanyListItem>
          eyebrow="Escopo da concessão"
          title="Escolher Empresa"
          subtitle="O papel valerá apenas dentro desta Empresa."
          placeholder="Nome, CNPJ ou subdomínio…"
          useSearch={useCompanySearch}
          getKey={(company) => company.id}
          toRow={(company) => ({
            primary: company.name,
            secondary: `${company.address.city}/${company.address.state}`,
          })}
          rowIcon={Icons.building}
          idleMessage="Busque a Empresa pelo nome, CNPJ ou subdomínio."
          notFoundMessage="Nenhuma Empresa encontrada."
          onSelect={(company) => patch(picking.key, { companyId: company.id, companyName: company.name })}
          onClose={() => setPicking(null)}
        />
      )}
      {picking?.scope === 'holding' && (
        <RecordPicker<HoldingOption>
          eyebrow="Escopo da concessão"
          title="Escolher Holding"
          subtitle="O papel valerá em todas as Empresas desta Holding."
          placeholder="Nome da holding…"
          useSearch={useHoldingSearch}
          getKey={(holding) => holding.id}
          toRow={(holding) => ({ primary: holding.name, secondary: 'Holding' })}
          rowIcon={Icons.branch}
          idleMessage="Busque a Holding pelo nome."
          notFoundMessage="Nenhuma Holding encontrada."
          onSelect={(holding) => patch(picking.key, { holdingId: holding.id, holdingName: holding.name })}
          onClose={() => setPicking(null)}
        />
      )}
    </>
  );
}

/** Props for {@link GrantRow}. */
interface GrantRowProps {
  grant: GrantDraft;
  roles: AuthorizationRole[];
  error: string | null;
  canReadCompanies: boolean;
  canManageUsers: boolean;
  /** Whether the operator is unscoped, and so may grant something that reaches every Empresa. */
  canGrantGlobally: boolean;
  onChange: (changes: Partial<GrantDraft>) => void;
  onPick: (scope: 'company' | 'holding') => void;
  onRemove: () => void;
}

/** One role being granted: which role, how far it reaches, and until when. */
function GrantRow({ grant, roles, error, canReadCompanies, canManageUsers, canGrantGlobally, onChange, onPick, onRemove }: GrantRowProps) {
  const scopeOptions: { value: GrantScope; label: string }[] = [
    ...(canGrantGlobally ? [{ value: 'global' as const, label: 'Todas as Empresas' }] : []),
    ...(canReadCompanies ? [{ value: 'company' as const, label: 'Uma Empresa' }] : []),
    ...(canManageUsers ? [{ value: 'holding' as const, label: 'Uma Holding' }] : []),
  ];

  const scopeName = grant.scope === 'company' ? grant.companyName : grant.scope === 'holding' ? grant.holdingName : null;

  return (
    <div style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', padding: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <SelectField
            label="Papel"
            required
            value={grant.roleId}
            onChange={(roleId) => onChange({ roleId })}
            options={roles.map((role) => ({ value: role.id, label: role.name }))}
            placeholder="Selecione o papel"
          />
        </div>
        <div style={{ width: 170 }}>
          <TextField
            label="Validade"
            type="date"
            value={grant.expiresOn}
            onChange={(expiresOn) => onChange({ expiresOn })}
          />
        </div>
        <button type="button" onClick={onRemove} className="pa-icon-btn" style={{ width: 34, height: 34, marginBottom: 2 }} title="Remover papel">
          <StrokeIcon d={Icons.x} size={15} />
        </button>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        {scopeOptions.length === 0 ? (
          // No reachable scope at all: a scoped operator who can read neither directory. Every grant they
          // could compose would be refused, so the screen says so instead of offering the form.
          <span style={{ fontSize: 12.5, color: 'var(--danger)' }}>
            Não há alcance que você possa conceder. Peça a atribuição a quem tem acesso amplo.
          </span>
        ) : (
          <>
            <Segmented<GrantScope>
              size="sm"
              options={scopeOptions}
              value={grant.scope}
              onChange={(scope) => onChange({ scope })}
            />
            {(grant.scope === 'company' || grant.scope === 'holding') && (
              <button type="button" onClick={() => onPick(grant.scope === 'company' ? 'company' : 'holding')} className="pa-link-btn" style={{ fontSize: 13 }}>
                {scopeName ?? (grant.scope === 'company' ? 'Escolher Empresa' : 'Escolher Holding')}
              </button>
            )}
            {!canGrantGlobally && (
              <span style={{ fontSize: 12, color: 'var(--fg-subtle)' }}>
                Seu acesso é restrito, então a concessão também precisa ser.
              </span>
            )}
          </>
        )}
      </div>

      {error && <div style={{ fontSize: 12, color: 'var(--danger)' }}>{error}</div>}
    </div>
  );
}
